import React, { useEffect, useMemo, useState } from 'react'
import Decimal from 'decimal.js'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'

import lang from '@/lang'
import CheckIcon from '@assets/images/icons/check.svg'
import BaseDrawer from '@/components/drawers/base-drawer'
import expenseConfig from '@/modules/expense/config'
import isEmpty from 'lodash/isEmpty'
import invoiceConfig from '@/modules/invoice/config'
import IconZoomMinus from '@assets/images/icons/zoom_minus.svg'
import IconZoomPlus from '@assets/images/icons/zoom_plus.svg'

import paymentConfig from '@/modules/payment/config'
import request from '@/modules/base/util/request'
import { bankAccountTypes, USER_RIGHTS, RECEIPT_PAYMENT_TYPES, transactionTypes } from '@/modules/base/util/constants'
import { FINANCE_API } from '@/modules/banking/constants'
import { fetchBankAccountList } from '@/redux/ducks/banking/bankAccountList'
import PaymentFormModel from '@/modules/payment/model/PaymentFormModel'
import PaymentForm from '@/modules/payment/components/payment-modal/payment-form'
import { CancelButton, DocumentControlButton } from '@/modules/base/components/buttons/index'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import {
  PAYMENT_TYPE_PAYMENT,
} from '@/modules/payment/constants'

import { toast } from '@/modules/base/components/toast/toastConfig'
import DocumentDetailPreview from '@/modules/document/components/edit/document-detail-preview'
import BaseDrawerPanelHeader from '@/components/drawers/base-drawer-panel-header/base-drawer-panel-header'
import InvoiceInfo from '@/modules/invoice/components/list/components/invoice-info'
import ExpenseInfo from '@/modules/expense/components/list/components/expense-info'
import ExpenseAndRevenueDetailPreview from '@/modules/expense-revenue/components/drawer/expense-and-revenue-detail-preview'
import RevenueInfo from '@/modules/revenue/list/components/revenue-info'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import moment from 'moment'
import { useNavigate } from 'react-router'
import navigateToCurrentPage from '@/utils/navigateToCurrentPage'

const { CASH_JOURNAL, CLEARING_ACCOUNT, OFFLINE_ACCOUNT } = FINANCE_API.ACCOUNT_TYPE
const {
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_CANCELLATION,
  TRANSACTION_TYPE_CASH_CREDIT_NOTE,
  TRANSACTION_TYPE_CASH_CANCELLATION,
  TRANSACTION_TYPE_EXPENSE,
  TRANSACTION_TYPE_REVENUE,
  TRANSACTION_TYPE_INVOICE,
  TRANSACTION_TYPE_MANUAL_CREDIT_NOTE,
} = transactionTypes

const VALID_PAYMENT_ACCOUNTS = [OFFLINE_ACCOUNT, CASH_JOURNAL, CLEARING_ACCOUNT]

const { receiptBaseUrl } = expenseConfig

const useStyles = makeStyles((theme) => ({
  previewHeaderDocumentDetailPreview: {
    height: 80,
    backgroundColor: theme.backgroundColor,
  },
  detailContainer: {
    maxWidth: 'calc(100vw - 350px - 100px)',
    width: '750px',
    height: '100%',
  },
  mainContainer: {
    width: '400px',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    '& > *': {
      margin: '20px 0px 20px 20px',
    },
    '& a': {
      border: theme.border,
      backgroundColor: theme.white,
      minWidth: 45,
      minHeight: 45,
    },
    '& a:hover': {
      backgroundColor: theme.backgroundColor,
    },
  },
  button: {
    height: 40,
    maxHeight: 40,
    width: 40,
    maxWidth: 40,
    transition: '.2s',
  },
  secondViewWrapper: {
    height: '100%',
    width: 'auto',
  },
  drawerFooter: {
    '& > :first-child': {
      width: 150,
    },
    '& > *': {
      width: 215,
    },
  },
}))

const PaymentDrawer = (props) => {
  const {
    isOverDue,
    open,
    transaction,
    dunnings,
    withoutPdf,
    onClose,
    onConfirm,
    onAdd,
  } = props
  const navigate = useNavigate()
  const type = [TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(transaction.type)
    ? transaction.type
    : transaction.type.includes(TRANSACTION_TYPE_CREDIT_NOTE)
      ? TRANSACTION_TYPE_CREDIT_NOTE
      : TRANSACTION_TYPE_INVOICE

  const {
    bankAccountList: { bankAccounts: allBankAccounts },
  } = useSelector(state => state.banking)
  const dispatch = useDispatch()

  const [pdfUrl, setPdfUrl] = useState('')
  const [previewZoom, setPreviewZoom] = useState(1)
  const [file, setFile] = useState()

  const bankAccounts = useMemo(() => {
    return (allBankAccounts || []).filter(bankAccount => VALID_PAYMENT_ACCOUNTS.includes(bankAccount.accountType))
  }, [allBankAccounts])

  const classes = useStyles()

  const [openDrawer, setOpenDrawer] = useState(open)
  const payInvoiceAllowed = useCheckAccessRights(USER_RIGHTS.ACCOUNTING.INVOICE_PAYMENT)
  const payCreditNoteAllowed = useCheckAccessRights(USER_RIGHTS.ACCOUNTING.CREDIT_NOTE_PAYMENT)
  const payExpenseAllowed = useCheckAccessRights(USER_RIGHTS.ACCOUNTING.EXPENSE_PAYMENT)
  const payRevenueAllowed = useCheckAccessRights(USER_RIGHTS.ACCOUNTING.REVENUE_PAYMENT)

  const hasPaymentRights = useMemo(() => {
    switch (type) {
      case TRANSACTION_TYPE_EXPENSE:
        return payExpenseAllowed
      case TRANSACTION_TYPE_REVENUE:
        return payRevenueAllowed
      case TRANSACTION_TYPE_CREDIT_NOTE:
        return payCreditNoteAllowed
      case TRANSACTION_TYPE_INVOICE:
        return payInvoiceAllowed

      default:
        return payInvoiceAllowed
    }
  }, [type, payInvoiceAllowed, payCreditNoteAllowed, payExpenseAllowed, payRevenueAllowed])

  const zoom = (zoomStep) => {
    setPreviewZoom(Math.max(previewZoom + zoomStep, 0.25))
  }

  useEffect(() => {
    if (!withoutPdf && ![TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)) {
      request(`${type === TRANSACTION_TYPE_CREDIT_NOTE ? invoiceConfig.cancellationUrl : invoiceConfig.resourceUrl}/preview/${transaction.id}`, {
        isConcurrent: true,
        auth: true,
        method: 'GET',
      }).then(response => {
        const { pdf: buffer } = response.body.data
        const blob = new Blob([Buffer.from(buffer)])

        const blobUrl = window.URL.createObjectURL(blob)
        setPdfUrl(currentPdfUrl => {
          window.URL.revokeObjectURL(currentPdfUrl)
          return blobUrl
        })
      }).catch((e) => {
        console.error(e)
        return toast.showToast({ type: 'error', message: lang.transactionEdit.transactionPreview.createPreviewErrorMessage })
      })
    } else if (!withoutPdf && [TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)) {
      const receiptData = transaction.receipts.map(receipt => {
        return {
          ...receipt,
          url: `${receiptBaseUrl}${receipt.url}`,
        }
      })
      setFile(receiptData)
    }

    if (allBankAccounts.length === 0) {
      dispatch(fetchBankAccountList())
    }

    return () => {
      if (!withoutPdf && ![TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)) {
        window.URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [])

  useEffect(() => {
    setOpenDrawer(open)
  }, [open])

  const payment = useMemo(() => {
    let payment = null
    switch (transaction.type) {
      case TRANSACTION_TYPE_CREDIT_NOTE:
      case TRANSACTION_TYPE_CANCELLATION:
      case TRANSACTION_TYPE_CASH_CREDIT_NOTE:
      case TRANSACTION_TYPE_CASH_CANCELLATION:
        payment = PaymentFormModel.fromCreditNote(transaction, bankAccounts)
        payment.amount = new Decimal(payment.amount).times(-1).toDP(2).toNumber()
        break
      case TRANSACTION_TYPE_MANUAL_CREDIT_NOTE:
        payment = PaymentFormModel.fromManualCreditNote(transaction, bankAccounts)
        break
      case TRANSACTION_TYPE_EXPENSE:
        payment = PaymentFormModel.fromExpense(transaction, bankAccounts)
        break
      case TRANSACTION_TYPE_REVENUE:
        payment = PaymentFormModel.fromRevenue(transaction, bankAccounts)
        break

      default:
        payment = PaymentFormModel.fromInvoice(transaction, bankAccounts)
        break
    }
    return payment
  }, [transaction, bankAccounts])

  const dunning = dunnings.length > 0 && dunnings[0]
  if (dunning) {
    dunning.label = !isEmpty(transaction.metaData.currentDunning)
      ? transaction.metaData.currentDunning.label
      : null
  }

  const [formData, setFormData] = useState(payment)
  // hack to ensure state is updated if bankacounts are loaded in the background
  useEffect(() => {
    if (bankAccounts.length) {
      setFormData(payment)
    }
  }, [bankAccounts])
  const [selectedProceeding, setSelectedProceeding] = useState(PAYMENT_TYPE_PAYMENT)
  const [formSubmitting, setFormSubmitting] = useState(false)

  const validateForm = () => {
    const {
      transactionNumber,
      customerName,
      date,
      amount,
      bankAccountId,
    } = formData

    return amount === 0
      || !customerName
      || !transactionNumber
      || !bankAccountId
      || !date

  }
  const handleFormDataChange = (value, name) => {
    setFormData({ ...formData, [name]: value })
  }
  const handleProceedingClick = (proceeding) => {
    setSelectedProceeding(proceeding)
  }
  const handleCancelClick = () => {
    onClose && onClose()
  }
  const handleSubmitClick = () => {
    if (formSubmitting) { return false }

    const { bankAccountId } = formData

    const selectedBankAccount = bankAccounts.find(account => bankAccountId === account.id)
    if (selectedBankAccount.accountType === CASH_JOURNAL && !selectedBankAccount.initialBalanceSubmitted) {
      setFormSubmitting(false)
      return toast.showToast({ type: 'error', message: lang.paymentNoCashJournalBalanceDefinedMessage })
    }

    const requestData = {
      ...payment,
      ...formData,
      type: selectedProceeding,
    }

    let url
    switch (transaction.type) {
      case TRANSACTION_TYPE_CREDIT_NOTE:
      case TRANSACTION_TYPE_CANCELLATION:
      case TRANSACTION_TYPE_CASH_CREDIT_NOTE:
      case TRANSACTION_TYPE_CASH_CANCELLATION:
        url = paymentConfig.createAddPaymentForCreditNoteUrl(transaction.id)
        requestData.amount = new Decimal(requestData.amount).times(-1).toDP(2).toNumber()
        break
      case TRANSACTION_TYPE_EXPENSE:
        url = paymentConfig.createAddPaymentForExpenseUrl(transaction.id)
        requestData.amount = new Decimal(requestData.amount).times(-1).toDP(2).toNumber()
        break
      case TRANSACTION_TYPE_REVENUE:
        url = paymentConfig.createAddPaymentForRevenueUrl(transaction.id)
        break
      case TRANSACTION_TYPE_MANUAL_CREDIT_NOTE:
        url = paymentConfig.createAddPaymentForInvoiceUrl(transaction.id)
        requestData.amount = new Decimal(requestData.amount).toDP(2).toNumber()
        break

      default:
        url = paymentConfig.createAddPaymentForInvoiceUrl(transaction.id)
        break
    }

    // handle validation for cashjournal
    if (selectedBankAccount.accountType === bankAccountTypes.CASH_JOURNAL) {
      const { amount, date } = requestData
      const { accountBalance, initialBalanceDate } = selectedBankAccount
      let errorMessage
      if ((accountBalance + amount) < 0) {
        errorMessage = lang.expenseSaveCashBalanceNegativeError
      }
      if (moment(initialBalanceDate).isAfter(date)) {
        errorMessage = lang.expenseSavePaymentDatePastInitialBalanceDateError
      }

      if (errorMessage) {
        return toast.showToast({ type: 'error', message: errorMessage })
      }
    }

    // when defined, called instead of sending requests
    if (onAdd) {
      onAdd({
        ...requestData,
        bookingType: RECEIPT_PAYMENT_TYPES.BOOKING,
        bankAccountName: selectedBankAccount.accountName,
      })
      onClose && onClose()
    } else {
      request(url, { method: 'POST', auth: true, data: requestData })
        .then(() => {
          toast.showToast({ message: lang.paymentSaveSuccessMessage })
          navigateToCurrentPage(navigate, location)
          onConfirm?.() || onClose?.()
        })
        .catch((error) => {
          if (error.statusCode === 401) return toast.showToast({ type: 'error', message: lang.paymentTimeoutMessage })
          toast.showToast({ type: 'error', message: lang.paymentSaveErrorMessage })
        })
    }

    setFormSubmitting(true)
  }

  const abortButtonLabel = lang.cancelButtonLabel
  const submitButtonLabel = onAdd
    ? lang.payments.paymentDrawer.addPayment
    : lang.payments.paymentDrawer.submitButton

  const actions = [
    <CancelButton
      type='cancel'
      label={abortButtonLabel}
      onClick={handleCancelClick} />,
    <ButtonIconSquare
      customClass={classes.submitButton}
      type='submit'
      icon={CheckIcon}
      disabled={validateForm() || !hasPaymentRights || formSubmitting}
      label={submitButtonLabel}
      onClick={handleSubmitClick} />,
  ]

  const drawerSecondViewHeader = !withoutPdf && ![TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)
    ? (
      <div className={classes.previewHeader}>
        <DocumentControlButton
          icon={IconZoomMinus}
          title={lang.expense.previewToolbar.items.zoomOut}
          customClass={classes.button}
          onClick={() => zoom(-0.1)} />
        <DocumentControlButton
          icon={IconZoomPlus}
          title={lang.expense.previewToolbar.items.zoomIn}
          customClass={classes.button}
          onClick={() => zoom(+0.1)} />
      </div>
    )
    : null

  let secondView = null
  if (!withoutPdf) {
    switch (type) {
      case TRANSACTION_TYPE_EXPENSE:
      case TRANSACTION_TYPE_REVENUE:
        secondView = (<ExpenseAndRevenueDetailPreview
          classNamePreviewHeader={classes.previewHeaderDocumentDetailPreview}
          classNames={classes.detailContainer} file={file}
          onZoom={zoom} downloadDisabled={true}
          zoom={previewZoom} />)
        break

      default:
        secondView = (
          <>
            <BaseDrawerPanelHeader showCloseIcon={false} children={drawerSecondViewHeader} />
            {pdfUrl && <DocumentDetailPreview
              classNames={classes.detailContainer}
              disableActions
              file={{ url: pdfUrl, mimetype: 'application/pdf' }}
              onZoom={zoom}
              downloadLabel={lang.generics.download}
              downloadUrl={pdfUrl}
              zoom={previewZoom}
              hideControls
            />}
          </>
        )
        break
    }
  }

  const handleClose = () => {
    setOpenDrawer(false)
    onClose && onClose()
  }

  const infoComponent = type === TRANSACTION_TYPE_EXPENSE
    ? <ExpenseInfo transaction={transaction} isOverDue={isOverDue} />
    : type === TRANSACTION_TYPE_REVENUE
      ? <RevenueInfo transaction={transaction} />
      : <InvoiceInfo transaction={transaction} isOverDue={isOverDue} absolute />

  return (
    <BaseDrawer
      withFooterLine
      open={openDrawer}
      enableBackdropClick
      onClose={handleClose}
      secondView={secondView}
      classNameSecondView={classes.secondViewWrapper}
      headerChildren={lang.payments.paymentDrawer.title}
      footerChildren={actions}
      footerChildrenClass={classes.drawerFooter}
    >
      <div className={classes.mainContainer}>
        <>
          {infoComponent}
          <PaymentForm
            hideInvoiceInfo
            hideTitle
            data={formData}
            bankAccounts={bankAccounts}
            dunning={dunning}
            difference={new Decimal(payment.amount).minus(formData.amount || 0).toDP(2).toNumber()}
            onFormDataChange={handleFormDataChange}
            proceeding={selectedProceeding}
            onProceedingClick={handleProceedingClick}
            readOnly={!hasPaymentRights}
          />
        </>
      </div>
    </BaseDrawer>
  )
}

PaymentDrawer.propTypes = {
  isOverDue: PropTypes.bool,
  open: PropTypes.bool,
  transaction: PropTypes.object,
  dunnings: PropTypes.array,
  withoutPdf: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
}

export default PaymentDrawer
